/* eslint-disable no-console */
import { Button, Grid, Stack, Typography } from '@mui/material';
import get from 'lodash-es/get';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { contractFormText } from '../../constants/contracts';
import { useConfig } from '../../hooks/useConfig';
import { useConstants } from '../../hooks/useConstants';
import { useContractConfigurationContext } from '../../hooks/useContractConfigurationContext';
import DatePicker from '../forms/DatePicker';
import FormSection from '../forms/FormSection';
import FormSubsection from '../forms/FormSubsection';
import S3Upload from '../forms/S3Upload';
import Select from '../forms/Select';
import TextField from '../forms/TextField';
import BreakingFlex from '../shared/BreakingFlex';

function ContractGeneralSection() {
  const { getValues, setValue, watch } = useFormContext();

  const contractId = getValues('contractId');
  const isNewContract = Boolean(contractId) === false;
  const { apiHost } = useConfig();
  const { constants } = useConstants();
  const STAGE = get(constants, 'RAILS.STAGE');
  const contractConfigurationContext = useContractConfigurationContext();

  const setContractName = get(contractConfigurationContext, 'setContractName');

  const updateContractName = (e) => {
    const contractName = e.target.value;
    setContractName(contractName);
    return e;
  };

  // values extracted from formState
  const [ioFile, invoicingNotes, bidSourceType] = watch(['ioFile', 'invoicingNotes', 'bidSourceType']);

  const [invoicingNotesOpen, setInvoicingNotesOpen] = useState(invoicingNotes || false);

  const BID_SOURCE_TYPE = get(constants, 'CONTRACT.BID_SOURCE_TYPE', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));
  const PMP_ALLOWED_EXCHANGES = get(constants, 'CONTRACT.PMP_ALLOWED_EXCHANGES', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));

  const VERTICALS = get(constants, 'CONTRACT.VERTICALS', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));

  // ensure dates are chronological
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  const generateKey = () => `${STAGE}/ios/${isNewContract ? 'tmp' : `contract=${contractId}`}`;

  return (
    <FormSection title={contractFormText.sectionTitles.general}>
      <FormSubsection>
        <Stack spacing={1}>
          <TextField
            fullWidth
            label="Contract Name"
            name="contractName"
            autoComplete="on"
            normalizeOutput={updateContractName}
          />
          {invoicingNotes || invoicingNotesOpen ? (
            <TextField
              name="invoicingNotes"
              id="invoicingNotes"
              fullWidth
              multiline
              rows={4}
              maxRows={4}
              label="Notes"
              helperText="only visible to you and your team"
            />
          ) : (
            <Stack direction="row" justifyContent="space-between">
              <Button variant="text" onClick={() => setInvoicingNotesOpen(true)}>
                Add Contract Notes (Optional)
              </Button>
            </Stack>
          )}
        </Stack>
      </FormSubsection>
      <FormSubsection>
        <Grid container>
          <BreakingFlex breakpoint="md">
            <Grid item xs={6}>
              <Stack spacing={2}>
                <Typography variant="body2">{contractFormText.subsectionTitles.flightDates}</Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <DatePicker
                    label="Start Date"
                    name="startDate"
                    rules={{
                      required: { value: true, message: 'Start date is required' },
                    }}
                    maxDate={endDate}
                  />
                  <Typography variant="caption" sx={{ mt: '12px !important' }}>
                    To
                  </Typography>
                  <DatePicker
                    label="End Date"
                    name="endDate"
                    rules={{
                      required: { value: true, message: 'End date is required' },
                    }}
                    minDate={startDate}
                  />
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={6}>
              <Stack spacing={2}>
                <Typography variant="body2">{contractFormText.subsectionTitles.bidSource}</Typography>
                <Stack direction="row" spacing={2}>
                  <Grid item xs={6}>
                    <Select fullWidth name="bidSourceType" label="Bid Source Type" options={BID_SOURCE_TYPE} />
                  </Grid>
                  <Grid item xs={6}>
                    {bidSourceType === 'PMP' && (
                      <Select
                        fullWidth
                        name="pmpAllowedExchanges"
                        label="PMP Allowed Exchanges"
                        rules={{ required: { value: true, message: 'PMP Allowed Exchanges is required' } }}
                        options={PMP_ALLOWED_EXCHANGES}
                      />
                    )}
                  </Grid>
                </Stack>
              </Stack>
            </Grid>
          </BreakingFlex>
        </Grid>
      </FormSubsection>
      <FormSubsection title={contractFormText.subsectionTitles.identification}>
        <Grid container>
          <BreakingFlex breakpoint="md">
            <Grid item xs={3}>
              <Select fullWidth name="vertical" label="Vertical" options={VERTICALS} />
            </Grid>
            <Grid item xs={3}>
              <TextField label="PO Number" fullWidth name="purchaseOrderNumber" />
            </Grid>
            <Grid item xs={3}>
              <TextField label="IO Number" fullWidth name="insertionOrderNumber" />
            </Grid>
            <Grid item xs={3}>
              <Stack spacing={2} width={1} alignItems="center">
                <S3Upload
                  s3InfoUrl="/admin/helpers/s3_upload_info/ventura"
                  generateKey={generateKey}
                  preserveName
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  label="Drag/Drop Here Or Click To Upload IO"
                  isReplace={ioFile}
                  replaceLabel="Drag/Drop Here Or Click To Replace IO"
                  name="ioFile"
                  onSuccess={(linksParam, pathParam) =>
                    setValue('ioFile', pathParam, { shouldValidate: true, shouldDirty: true })
                  }
                />
                <Button
                  disabled={!ioFile}
                  onClick={() =>
                    ioFile &&
                    window.open(`${apiHost}/admin/helpers/s3_get_resource/${encodeURIComponent(ioFile)}`, '_blank')
                  }
                >
                  View Uploaded IO
                </Button>
              </Stack>
            </Grid>
          </BreakingFlex>
        </Grid>
      </FormSubsection>
    </FormSection>
  );
}

export default ContractGeneralSection;
